/**
 * Minimal numeric linear algebra for pipe fitting — symmetric 3×3 eigen
 * decomposition plus small Vec3 helpers. Pure, allocation-light, THREE-free.
 *
 * The eigensolver is the classic cyclic **Jacobi rotation** method. It's only
 * ever called on 3×3 symmetric matrices (covariance of normals / neighbour
 * offsets), where it converges in a handful of sweeps and is numerically
 * bullet-proof — far simpler to get right than a general QR iteration, and we
 * don't need anything fancier for 3×3.
 */

import type { Vec3 } from './types';

export const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];

export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

export const norm = (a: Vec3): number => Math.sqrt(dot(a, a));

export const normalize = (a: Vec3): Vec3 => {
  const n = norm(a);
  if (n < 1e-20) return [0, 0, 0];
  return [a[0] / n, a[1] / n, a[2] / n];
};

/** A symmetric 3×3 matrix stored as its 6 unique entries. */
export interface Sym3 {
  xx: number;
  yy: number;
  zz: number;
  xy: number;
  xz: number;
  yz: number;
}

export const zeroSym3 = (): Sym3 => ({ xx: 0, yy: 0, zz: 0, xy: 0, xz: 0, yz: 0 });

/** Accumulate the outer product `v vᵀ` into `m` (m += v vᵀ). */
export const addOuter = (m: Sym3, v: Vec3): void => {
  m.xx += v[0] * v[0];
  m.yy += v[1] * v[1];
  m.zz += v[2] * v[2];
  m.xy += v[0] * v[1];
  m.xz += v[0] * v[2];
  m.yz += v[1] * v[2];
};

export interface Eigen3 {
  /** Eigenvalues, ascending (values[0] ≤ values[1] ≤ values[2]). */
  values: [number, number, number];
  /** Corresponding unit eigenvectors (columns), same order as `values`. */
  vectors: [Vec3, Vec3, Vec3];
}

/**
 * Symmetric 3×3 eigen-decomposition via cyclic Jacobi rotations. Returns
 * eigenpairs sorted by ascending eigenvalue. Robust for any real symmetric
 * input; converges to machine precision in well under 20 sweeps for 3×3.
 */
export function eigenSym3(m: Sym3): Eigen3 {
  // Working full matrix.
  const a = [
    [m.xx, m.xy, m.xz],
    [m.xy, m.yy, m.yz],
    [m.xz, m.yz, m.zz],
  ];
  // Eigenvector accumulator (starts as identity).
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  const rotate = (p: number, q: number) => {
    const apq = a[p][q];
    if (Math.abs(apq) < 1e-300) return;
    const app = a[p][p];
    const aqq = a[q][q];
    const phi = 0.5 * Math.atan2(2 * apq, aqq - app);
    const c = Math.cos(phi);
    const s = Math.sin(phi);

    // Apply the Givens rotation A' = Rᵀ A R for the (p,q) plane.
    for (let i = 0; i < 3; i++) {
      const aip = a[i][p];
      const aiq = a[i][q];
      a[i][p] = c * aip - s * aiq;
      a[i][q] = s * aip + c * aiq;
    }
    for (let i = 0; i < 3; i++) {
      const api = a[p][i];
      const aqi = a[q][i];
      a[p][i] = c * api - s * aqi;
      a[q][i] = s * api + c * aqi;
    }
    // Accumulate the rotation into the eigenvector matrix.
    for (let i = 0; i < 3; i++) {
      const vip = v[i][p];
      const viq = v[i][q];
      v[i][p] = c * vip - s * viq;
      v[i][q] = s * vip + c * viq;
    }
  };

  for (let sweep = 0; sweep < 20; sweep++) {
    const off = Math.abs(a[0][1]) + Math.abs(a[0][2]) + Math.abs(a[1][2]);
    if (off < 1e-20) break;
    rotate(0, 1);
    rotate(0, 2);
    rotate(1, 2);
  }

  const eigvals: Array<{ val: number; vec: Vec3 }> = [
    { val: a[0][0], vec: [v[0][0], v[1][0], v[2][0]] },
    { val: a[1][1], vec: [v[0][1], v[1][1], v[2][1]] },
    { val: a[2][2], vec: [v[0][2], v[1][2], v[2][2]] },
  ];
  eigvals.sort((x, y) => x.val - y.val);

  return {
    values: [eigvals[0].val, eigvals[1].val, eigvals[2].val],
    vectors: [normalize(eigvals[0].vec), normalize(eigvals[1].vec), normalize(eigvals[2].vec)],
  };
}

/**
 * Build an orthonormal basis (e1, e2) for the plane perpendicular to `axis`.
 * Used to project 3-D surface points down to the 2-D cross-section for the
 * circle fit. The choice of in-plane axes is arbitrary but consistent.
 */
export function planeBasis(axis: Vec3): { e1: Vec3; e2: Vec3 } {
  const a = normalize(axis);
  // Pick the world axis least aligned with `a` to avoid a degenerate cross.
  const ax = Math.abs(a[0]);
  const ay = Math.abs(a[1]);
  const az = Math.abs(a[2]);
  const ref: Vec3 = ax <= ay && ax <= az ? [1, 0, 0] : ay <= az ? [0, 1, 0] : [0, 0, 1];
  const e1 = normalize(cross(a, ref));
  const e2 = normalize(cross(a, e1));
  return { e1, e2 };
}
